import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  Link as ChakraLink,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuArrowRight, LuMail, LuMapPin, LuPhone } from "react-icons/lu";
import { FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";

import CampFoto from "assets/Camp.jpg";
import { Button } from "components/ui/Button";
import AppAvatar from "components/utils/AppAvatar";
import { useMainContactoJefesPublicos } from "features/main/contacto/hooks";
import { SectionEyebrow, SectionHeading, SectionShell } from "../Inicio/components";

const CLUB_PHONE = "81 1979 7902";
const CLUB_EMAIL = "awk@arawaks.org";
const CLUB_ADDRESS = "Rio Danubio 128, Del Valle, San Pedro, N.L.";
const MAP_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3596.4356859594013!2d-100.37654242477281!3d25.65684827741799!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8662bd92e54499b7%3A0x55ee18bdcc02ec0f!2sArawaks!5e0!3m2!1sen!2smx!4v1735614518815!5m2!1sen!2smx";
const SOCIAL_LINKS = [
  { label: "Instagram", href: "https://www.instagram.com/arawaks/", icon: FaInstagram },
  { label: "Facebook", href: "https://www.facebook.com/clubarawaks", icon: FaFacebookF },
  { label: "YouTube", href: "https://www.youtube.com/@clubarawaks7844", icon: FaYoutube },
];

const DIRECTORES_PLACEHOLDER = [
  { id: "director-general", nombre: "Por confirmar", cargo: "Director general", telefono: null },
  { id: "director-operativo", nombre: "Por confirmar", cargo: "Director operativo", telefono: null },
  { id: "director-ejecutivo", nombre: "Por confirmar", cargo: "Director ejecutivo", telefono: null },
];

function phoneHref(phone) {
  const digits = String(phone || "").replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : undefined;
}

function gradeRank(nivelNombre) {
  const match = String(nivelNombre || "").match(/\d+/);
  return match ? Number(match[0]) : 99;
}

function formatNivelLabel(nivelNombre, campusNombre) {
  const nivel = String(nivelNombre || "").trim() || "Nivel";
  const campus = String(campusNombre || "").trim();
  return campus ? `${nivel} - ${campus}` : nivel;
}

const ContactHero = () => (
  <Box as="section" position="relative" overflow="hidden" bg="public.bg.canvas" color="public.fg.default" px={{ base: 5, md: 10 }} pt={{ base: "82px", md: "116px" }} pb={{ base: 7, md: 11 }} borderBottomWidth="1px" borderColor="public.border.subtle">
    <Box position="absolute" w={{ base: "150px", md: "310px" }} h={{ base: "58px", md: "100px" }} rounded="full" bg="#dce8f1" _dark={{ bg: "#243746" }} right={{ base: "-78px", md: "8%" }} bottom={{ base: "-28px", md: "-54px" }} transform="rotate(-9deg)" opacity=".72" />
    <Stack position="relative" zIndex="1" maxW="7xl" mx="auto">
      <Heading as="h1" fontFamily="display" fontSize={{ base: "38px", md: "56px" }} lineHeight="1" letterSpacing="-.03em">Contacto</Heading>
    </Stack>
  </Box>
);

const ContactRow = ({ icon, title, children, href }) => (
  <HStack align="start" gap="4" py="4" borderBottomWidth="1px" borderColor="public.border.subtle" _last={{ borderBottomWidth: 0 }}>
    <Box display="grid" placeItems="center" boxSize="11" rounded="16px" bg="brand.redSubtle" color="brand.redFg" flexShrink="0">
      <Icon as={icon} boxSize="5" />
    </Box>
    <Stack gap="0.5" minW="0">
      <Text fontSize="sm" color="public.fg.muted" fontWeight="800" textTransform="uppercase">
        {title}
      </Text>
      {href ? (
        <ChakraLink href={href} color="public.fg.default" fontWeight="800" lineHeight="1.6" wordBreak="break-word">
          {children}
        </ChakraLink>
      ) : (
        <Text color="public.fg.default" fontWeight="800" lineHeight="1.6">
          {children}
        </Text>
      )}
    </Stack>
  </HStack>
);

const PersonCard = ({ name, label, phone, imageUrl }) => (
  <Card.Root bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" rounded="24px" h="full" overflow="hidden">
    <Card.Body p="5">
      <Stack gap="5" h="full">
        <HStack gap="3" align="start" minW="0">
          <AppAvatar name={name} src={imageUrl} size={58} />
          <Stack gap="1" minW="0">
            <Heading as="h3" fontFamily="display" fontSize="xl" lineHeight="1.1">
              {name}
            </Heading>
            <Badge bg="public.bg.muted" color="public.fg.default" borderWidth="1px" borderColor="public.border.default" rounded="full" px="3" py="1" alignSelf="start">
              {label}
            </Badge>
          </Stack>
        </HStack>

        <HStack color="public.fg.muted" gap="2.5" mt="auto">
          <Icon as={LuPhone} boxSize="4" color="brand.red" />
          {phone ? (
            <ChakraLink href={phoneHref(phone)} fontSize="sm" fontWeight="800">
              {phone}
            </ChakraLink>
          ) : (
            <Text fontSize="sm" fontWeight="800">
              Telefono por definir
            </Text>
          )}
        </HStack>
      </Stack>
    </Card.Body>
  </Card.Root>
);

const JefeCard = ({ nivel }) => {
  const jefe = nivel.jefe;
  const phone = jefe?.telefono || null;
  const name = jefe?.nombre || "Por asignar";

  return (
    <PersonCard
      name={name}
      label={formatNivelLabel(nivel.nivelNombre, nivel.campusNombre)}
      phone={phone}
      imageUrl={jefe?.fotoUrl ?? null}
    />
  );
};

const DirectoresSection = () => (
  <SectionShell bg="public.bg.canvas">
    <Stack gap="8">
      <SectionHeading
        eyebrow="Directores"
        title="Direccion del club."
        description="Estos contactos quedan como placeholders hasta conectar la fuente real de directores."
      />
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="5">
        {DIRECTORES_PLACEHOLDER.map((director) => (
          <PersonCard key={director.id} name={director.nombre} label={director.cargo} phone={director.telefono} />
        ))}
      </SimpleGrid>
    </Stack>
  </SectionShell>
);

const JefesSection = () => {
  const q = useMainContactoJefesPublicos();
  const niveles = React.useMemo(() => {
    const rows = Array.isArray(q.data?.niveles) ? q.data.niveles : [];
    const campusCounts = rows.reduce((acc, row) => {
      const key = row?.campusId || row?.campusNombre || "";
      acc.set(key, (acc.get(key) || 0) + 1);
      return acc;
    }, new Map());

    return [...rows].sort((a, b) => {
      const aCampusKey = a?.campusId || a?.campusNombre || "";
      const bCampusKey = b?.campusId || b?.campusNombre || "";
      const byCampusSize = (campusCounts.get(bCampusKey) || 0) - (campusCounts.get(aCampusKey) || 0);
      if (byCampusSize) return byCampusSize;
      const byCampusName = String(a?.campusNombre || "").localeCompare(String(b?.campusNombre || ""));
      if (byCampusName) return byCampusName;
      const byGrade = gradeRank(a?.nivelNombre) - gradeRank(b?.nivelNombre);
      if (byGrade) return byGrade;
      return String(a?.nivelNombre || "").localeCompare(String(b?.nivelNombre || ""));
    });
  }, [q.data?.niveles]);

  return (
    <SectionShell bg="public.bg.panel">
      <Stack gap="8">
        <SectionHeading
          eyebrow="Jefes de nivel"
          title="Contacto por grado."
          description="Contacta al jefe del nivel de tu hijo para dudas, avisos o para ser agregado al grupo de WhatsApp correspondiente."
        />

        {q.isLoading ? (
          <Flex justify="center" py="12">
            <Spinner size="xl" color="brand.red" />
          </Flex>
        ) : null}

        {q.isError ? (
          <Card.Root bg="design.bg.canvas" borderWidth="1px" borderColor="design.border.default" rounded="card">
            <Card.Body p="8">
              <Text color="red.500" textAlign="center">
                No fue posible cargar los jefes de nivel.
              </Text>
            </Card.Body>
          </Card.Root>
        ) : null}

        {!q.isLoading && !q.isError && niveles.length ? (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="5">
            {niveles.map((nivel) => (
              <JefeCard key={nivel.nivelId} nivel={nivel} />
            ))}
          </SimpleGrid>
        ) : null}

        {!q.isLoading && !q.isError && !niveles.length ? (
          <Card.Root bg="design.bg.canvas" borderWidth="1px" borderColor="design.border.default" rounded="card">
            <Card.Body p="8">
              <Text color="design.fg.muted" textAlign="center">
                No hay niveles disponibles para el semestre actual.
              </Text>
            </Card.Body>
          </Card.Root>
        ) : null}
      </Stack>
    </SectionShell>
  );
};

const VisitSection = () => (
  <SectionShell>
    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 8, lg: 12 }} alignItems="stretch">
      <Stack gap="6">
        <SectionHeading
          eyebrow="Club Arawaks"
          title="Datos generales."
          description="Para dudas generales o informes del club, estos son los datos principales."
        />
        <Card.Root bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" rounded="28px">
          <Card.Body px={{ base: 5, md: 6 }} py="2">
            <ContactRow icon={LuMapPin} title="Direccion">
            {CLUB_ADDRESS}
            </ContactRow>
            <ContactRow icon={LuPhone} title="Telefono" href={phoneHref(CLUB_PHONE)}>
              {CLUB_PHONE}
            </ContactRow>
            <ContactRow icon={LuMail} title="Correo" href={`mailto:${CLUB_EMAIL}`}>
              {CLUB_EMAIL}
            </ContactRow>
            <Box py="4">
              <Text fontSize="sm" color="public.fg.muted" fontWeight="800" textTransform="uppercase" mb="3">
                Redes
              </Text>
              <HStack gap="3" wrap="wrap">
                {SOCIAL_LINKS.map((item) => (
                  <ChakraLink
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={item.label}
                    display="grid"
                    placeItems="center"
                    boxSize="11"
                    rounded="16px"
                    bg="brand.redSubtle"
                    color="brand.redFg"
                    _hover={{ bg: "brand.red", color: "white", textDecoration: "none" }}
                  >
                    <Icon as={item.icon} boxSize="5" />
                  </ChakraLink>
                ))}
              </HStack>
            </Box>
          </Card.Body>
        </Card.Root>
      </Stack>

      <Card.Root overflow="hidden" rounded="28px 14px 28px 14px" borderWidth="6px" borderColor="public.bg.elevated" bg="public.bg.elevated" minH={{ base: "360px", lg: "100%" }} shadow="cardLift">
        <Box
          as="iframe"
          title="Mapa de Club Arawaks"
          src={MAP_SRC}
          width="100%"
          height="100%"
          minH={{ base: "360px", lg: "100%" }}
          border="0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </Card.Root>
    </SimpleGrid>
  </SectionShell>
);

const ContactCta = () => (
  <Box as="section" bg="public.bg.canvas" px={{ base: 5, md: 10 }} py={{ base: 16, md: 24 }}><Box position="relative" overflow="hidden" maxW="6xl" mx="auto" rounded="48px 18px 48px 18px" bg="gray.950">
    <Box position="absolute" inset="0" bgImage={`url(${CampFoto})`} bgSize="cover" bgPosition="center" opacity="0.32" />
    <Box position="absolute" inset="0" bg="rgba(10,0,0,.72)" />
    <Stack position="relative" zIndex="1" maxW="4xl" mx="auto" px={{ base: 5, md: 10 }} py={{ base: 16, md: 22 }} textAlign="center" align="center" gap="6" color="white">
      <SectionEyebrow tone="dark" alignSelf="center">
        Proximo paso
      </SectionEyebrow>
      <Heading as="h2" fontFamily="display" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.02">
        Listo para empezar?
      </Heading>
      <Text color="whiteAlpha.800" fontSize={{ base: "md", md: "lg" }} lineHeight="1.7" maxW="2xl">
        Registra a tu hijo y recibe informacion de actividades, campamentos y eventos del club.
      </Text>
      <Button as={RouterLink} to="/registro" variant="solid" color="red" size="lg">
        Registrarme
        <Icon as={LuArrowRight} boxSize="4" />
      </Button>
    </Stack></Box>
  </Box>
);

const Contacto = () => (
  <Box bg="public.bg.canvas" color="public.fg.default">
    <ContactHero />
    <VisitSection />
    <JefesSection />
    <DirectoresSection />
    <ContactCta />
  </Box>
);

export default Contacto;
